// Demo / test STV results dataset — Malta 2022 General Election,
// District 13 (Gozo). The cleanest worked example from the count-study
// report: 17 candidates, 16 published counts, three first-count quotas,
// a long exclusion sequence, and a final fill-rule seat 190 votes short
// of quota.
//
// Running totals are taken verbatim from
//   docs/malta_2026_handover/handover/raw_data/counts_long_2022.csv
// (District 13, casual_election_id empty).
//
// NO `statusByCandidate` entries — the auto-deriver figures statuses
// out from the totals:
//   · R1: Camilleri Clint, Borg, Refalo cross quota → elected_by_quota.
//   · R5-R14: each round's missing candidate (operator dropped them
//     because the board cleared their column) auto-flips to excluded.
//   · R15: Abela crosses quota; Camilleri Abigail's column blank, auto-
//     excluded.
//   · R16 (last entered): lock condition fires — Said elected_without_
//     quota, Cutajar not_elected. Matches the commission's final call.
//
// If the operator ever wanted to override (e.g. show Said's call at
// R15 instead of R16), they would add a statusByCandidate entry. The
// default demo follows the textbook + commission convention exactly.
//
// CONTRACT (see ./contract.ts):
//   This dataset only renders when results_demo_mode === 'live' and the
//   site-wide DemoBanner + per-widget inline banners are active. The
//   hero winner is NEVER driven by demo mode — that one is always real.

import {
  emptyResultsState,
  type ResultsState,
  type StoredRound,
} from '../electionday-results-types';
import type { CanonicalParty } from '../../../api/_lib/parties';

const D13_DISTRICT_ID = 13;
const VALID_VOTES = 26_355; // quota = ⌊26355÷6⌋+1 = 4393

export interface DemoCandidate {
  id: string;
  name: string;
  party: CanonicalParty;
}

/** Roster for D13 — used to drive the seat strip + round table when
 *  demo mode is on. Order = first-count totals descending. */
export const DEMO_D13_CANDIDATES: readonly DemoCandidate[] = [
  { id: 'demo-d13-01', name: 'Camilleri, Clint', party: 'PL' },
  { id: 'demo-d13-02', name: 'Borg, Alex', party: 'PN' },
  { id: 'demo-d13-03', name: 'Refalo, Anton', party: 'PL' },
  { id: 'demo-d13-04', name: 'Said, Chris', party: 'PN' },
  { id: 'demo-d13-05', name: 'Abela, Jo Etienne', party: 'PL' },
  { id: 'demo-d13-06', name: 'Cutajar, Kevin', party: 'PN' },
  { id: 'demo-d13-07', name: 'Buttigieg, Claudette', party: 'PN' },
  { id: 'demo-d13-08', name: 'Ellis, Joseph', party: 'PN' },
  { id: 'demo-d13-09', name: 'Zammit, Christian', party: 'PL' },
  { id: 'demo-d13-10', name: 'Camilleri, Abigail', party: 'PL' },
  { id: 'demo-d13-11', name: 'Xuereb, Josephine', party: 'PN' },
  { id: 'demo-d13-12', name: 'Caruana, Luke', party: 'ADPD' },
  { id: 'demo-d13-13', name: 'Bonnici, Nazareno', party: 'IND' },
  { id: 'demo-d13-14', name: 'Florian, Stephen', party: 'Other' },
  { id: 'demo-d13-15', name: 'Buttigieg, David John', party: 'Other' },
  { id: 'demo-d13-16', name: 'Camilleri, George Paul', party: 'PL' },
  { id: 'demo-d13-17', name: 'Gheorghe, Radu', party: 'Other' },
] as const;

const ROUNDS: StoredRound[] = [
  {
    roundNumber: 1,
    actorId: null,
    runningTotalByCandidate: {
      'demo-d13-01': 6458, 'demo-d13-02': 6108, 'demo-d13-03': 5002,
      'demo-d13-04': 2772, 'demo-d13-05': 1803, 'demo-d13-06': 1177,
      'demo-d13-07': 609,  'demo-d13-08': 536,  'demo-d13-09': 415,
      'demo-d13-10': 394,  'demo-d13-11': 378,  'demo-d13-12': 331,
      'demo-d13-13': 157,  'demo-d13-14': 88,   'demo-d13-15': 63,
      'demo-d13-16': 38,   'demo-d13-17': 26,
    },
    nonTransferable: 0,
  },
  {
    roundNumber: 2,
    actorId: 'demo-d13-01', // Camilleri Clint surplus
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 6108, 'demo-d13-03': 5002,
      'demo-d13-04': 2807, 'demo-d13-05': 2895, 'demo-d13-06': 1199,
      'demo-d13-07': 620,  'demo-d13-08': 542,  'demo-d13-09': 763,
      'demo-d13-10': 873,  'demo-d13-11': 383,  'demo-d13-12': 336,
      'demo-d13-13': 166,  'demo-d13-14': 91,   'demo-d13-15': 65,
      'demo-d13-16': 84,   'demo-d13-17': 28,
    },
    nonTransferable: 0,
  },
  {
    roundNumber: 3,
    actorId: 'demo-d13-02', // Borg Alex surplus
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 5002,
      'demo-d13-04': 3341, 'demo-d13-05': 2923, 'demo-d13-06': 1690,
      'demo-d13-07': 943,  'demo-d13-08': 673,  'demo-d13-09': 775,
      'demo-d13-10': 880,  'demo-d13-11': 546,  'demo-d13-12': 349,
      'demo-d13-13': 174,  'demo-d13-14': 93,   'demo-d13-15': 66,
      'demo-d13-16': 85,   'demo-d13-17': 29,
    },
    nonTransferable: 0,
  },
  {
    roundNumber: 4,
    actorId: 'demo-d13-03', // Refalo Anton surplus
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3351, 'demo-d13-05': 3276, 'demo-d13-06': 1696,
      'demo-d13-07': 946,  'demo-d13-08': 675,  'demo-d13-09': 866,
      'demo-d13-10': 1004, 'demo-d13-11': 548,  'demo-d13-12': 350,
      'demo-d13-13': 179,  'demo-d13-14': 93,   'demo-d13-15': 66,
      'demo-d13-16': 96,   'demo-d13-17': 30,
    },
    nonTransferable: 0,
  },
  {
    roundNumber: 5,
    actorId: 'demo-d13-17', // Gheorghe Radu excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3352, 'demo-d13-05': 3277, 'demo-d13-06': 1696,
      'demo-d13-07': 946,  'demo-d13-08': 676,  'demo-d13-09': 867,
      'demo-d13-10': 1004, 'demo-d13-11': 548,  'demo-d13-12': 351,
      'demo-d13-13': 179,  'demo-d13-14': 95,   'demo-d13-15': 86,
      'demo-d13-16': 96,
    },
    nonTransferable: 3,
  },
  {
    roundNumber: 6,
    actorId: 'demo-d13-15', // Buttigieg David John excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3354, 'demo-d13-05': 3285, 'demo-d13-06': 1703,
      'demo-d13-07': 947,  'demo-d13-08': 677,  'demo-d13-09': 871,
      'demo-d13-10': 1009, 'demo-d13-11': 549,  'demo-d13-12': 367,
      'demo-d13-13': 182,  'demo-d13-14': 109,  'demo-d13-16': 96,
    },
    nonTransferable: 27,
  },
  {
    roundNumber: 7,
    actorId: 'demo-d13-16', // Camilleri George Paul excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3354, 'demo-d13-05': 3315, 'demo-d13-06': 1703,
      'demo-d13-07': 948,  'demo-d13-08': 678,  'demo-d13-09': 901,
      'demo-d13-10': 1038, 'demo-d13-11': 549,  'demo-d13-12': 367,
      'demo-d13-13': 183,  'demo-d13-14': 109,
    },
    nonTransferable: 31,
  },
  {
    roundNumber: 8,
    actorId: 'demo-d13-14', // Florian Stephen excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3360, 'demo-d13-05': 3319, 'demo-d13-06': 1708,
      'demo-d13-07': 948,  'demo-d13-08': 678,  'demo-d13-09': 907,
      'demo-d13-10': 1040, 'demo-d13-11': 553,  'demo-d13-12': 380,
      'demo-d13-13': 206,
    },
    nonTransferable: 77,
  },
  {
    roundNumber: 9,
    actorId: 'demo-d13-13', // Bonnici Nazareno excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3368, 'demo-d13-05': 3335, 'demo-d13-06': 1715,
      'demo-d13-07': 956,  'demo-d13-08': 681,  'demo-d13-09': 918,
      'demo-d13-10': 1047, 'demo-d13-11': 558,  'demo-d13-12': 405,
    },
    nonTransferable: 193,
  },
  {
    roundNumber: 10,
    actorId: 'demo-d13-12', // Caruana Luke excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3397, 'demo-d13-05': 3362, 'demo-d13-06': 1755,
      'demo-d13-07': 1001, 'demo-d13-08': 693,  'demo-d13-09': 957,
      'demo-d13-10': 1064, 'demo-d13-11': 576,
    },
    nonTransferable: 371,
  },
  {
    roundNumber: 11,
    actorId: 'demo-d13-11', // Xuereb Josephine excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3531, 'demo-d13-05': 3381, 'demo-d13-06': 1935,
      'demo-d13-07': 1158, 'demo-d13-08': 739,  'demo-d13-09': 962,
      'demo-d13-10': 1067,
    },
    nonTransferable: 403,
  },
  {
    roundNumber: 12,
    actorId: 'demo-d13-08', // Ellis Joseph excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3752, 'demo-d13-05': 3396, 'demo-d13-06': 2261,
      'demo-d13-07': 1290, 'demo-d13-09': 977,  'demo-d13-10': 1072,
    },
    nonTransferable: 428,
  },
  {
    roundNumber: 13,
    actorId: 'demo-d13-09', // Zammit Christian excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 3771, 'demo-d13-05': 3847, 'demo-d13-06': 2290,
      'demo-d13-07': 1303, 'demo-d13-10': 1426,
    },
    nonTransferable: 539,
  },
  {
    roundNumber: 14,
    actorId: 'demo-d13-07', // Buttigieg Claudette excluded
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 4181, 'demo-d13-05': 3860, 'demo-d13-06': 3091,
      'demo-d13-10': 1432,
    },
    nonTransferable: 612,
  },
  {
    roundNumber: 15,
    actorId: 'demo-d13-10', // Camilleri Abigail excluded; Abela hits quota
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 4196, 'demo-d13-05': 5107, 'demo-d13-06': 3111,
    },
    nonTransferable: 762,
  },
  {
    roundNumber: 16,
    actorId: 'demo-d13-05', // Abela's surplus distributed; commission closes the count
    runningTotalByCandidate: {
      'demo-d13-01': 4393, 'demo-d13-02': 4393, 'demo-d13-03': 4393,
      'demo-d13-04': 4203, 'demo-d13-05': 4393, 'demo-d13-06': 3119,
    },
    // No statusByCandidate — at this LAST round, the auto-deriver's
    // PASS 2 sees continuing = [Said 4203, Cutajar 3119], elected = 4,
    // seats remaining = 1, lock holds (Said > Cutajar + 0), so Said
    // auto-elects without quota and Cutajar auto-marks not elected.
    nonTransferable: 1461,
  },
];

/** Returns the demo ResultsState — populated D13 only, the other
 *  twelve districts empty (the public display shows "Awaiting count"
 *  for them, which is the truthful view of a count-night demo where
 *  only one district has been published). */
export function getDemoResultsState(): ResultsState {
  const empty = emptyResultsState();
  return {
    districts: {
      ...empty.districts,
      [String(D13_DISTRICT_ID)]: {
        validVotes: VALID_VOTES,
        rounds: ROUNDS,
      },
    },
    updatedAt: '2022-03-28T12:00:00.000Z',
  };
}

export const DEMO_D13_DISTRICT_ID = D13_DISTRICT_ID;
